/**
 * `deleteField()` sentinel re-exported so app code can request atomic field
 * removal through dot-notation update payloads without importing the Firebase
 * SDK directly.
 */
export { deleteField } from 'firebase/firestore';

/**
 * Utility function to remove undefined values from an object and convert model instances to plain objects
 * Firestore doesn't accept undefined values or custom class instances, so we need to exclude/convert them
 * Preserves Firestore FieldValue sentinels (serverTimestamp, deleteField, arrayUnion, ...)
 * to maintain proper write semantics
 */
export const removeUndefinedValues = (obj: any): any => {
  const cleaned: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      // Check if this is a Firestore FieldValue sentinel - preserve it as-is
      if (value && typeof value === 'object' && typeof (value as any)._methodName === 'string') {
        cleaned[key] = value;
      } else if (value instanceof Date) {
        // Pass Dates straight through so the Firestore SDK stores a Timestamp.
        // A Date is an object WITH toJSON() (→ ISO string); without this guard
        // the toJSON branch below spreads that string into a char-indexed map
        // ({0:'2',1:'0',…}), corrupting the field into a non-Timestamp object
        // that no Firestore range query can ever match.
        cleaned[key] = value;
      } else if (Array.isArray(value)) {
        // Handle arrays: recursively clean each element
        cleaned[key] = value.map(item => {
          if (item instanceof Date) {
            return item;
          } else if (item && typeof item === 'object') {
            // Check for Firestore FieldValue sentinels in array items
            if (typeof (item as any)._methodName === 'string') {
              return item;
            } else if ('toJSON' in item && typeof (item as any).toJSON === 'function') {
              // Convert model instances to plain objects first, then clean
              return removeUndefinedValues((item as any).toJSON());
            } else {
              // Recursively clean plain objects
              return removeUndefinedValues(item);
            }
          } else {
            return item;
          }
        });
      } else if (value && typeof value === 'object' && 'toJSON' in value && typeof (value as any).toJSON === 'function') {
        // Handle single model instances: convert to plain object and clean
        cleaned[key] = removeUndefinedValues((value as any).toJSON());
      } else if (value && typeof value === 'object') {
        // Handle plain objects: recursively clean them (but not server timestamps)
        cleaned[key] = removeUndefinedValues(value);
      } else {
        // Handle primitive values
        cleaned[key] = value;
      }
    }
  }
  return cleaned;
};
